import {
  BitkitActionMenu,
  BitkitButton,
  BitkitControlButton,
  BitkitExpandableCard,
  BitkitLink,
  IconArrowRight,
  IconBitbotIntercomWhite,
  IconCheck,
  IconCheckCircleFilled,
  IconCircleDashed,
  IconCross,
  IconEyeSlash,
  IconMoreVertical,
  rem,
} from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { Card } from '@chakra-ui/react/card';
import { Image } from '@chakra-ui/react/image';
import { chakra } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import { useEffect, useRef, useState } from 'react';

import URLs from '@/enums/urls';

import onboardingCompleteIllustration from './assets/onboarding-complete.svg';
import {
  OnboardingChecklistStepId,
  trackChecklistCollapsed,
  trackChecklistDisplayed,
  trackChecklistItemExpanded,
} from './onboardingChecklistTracking';

export type OnboardingStepStatus = 'completed' | 'active' | 'upcoming';

type OnboardingStepCta = {
  label: string;
  onClick?: () => void;
  href?: string;
};

export type OnboardingStep = {
  id: string;
  title: string;
  description: string;
  status: OnboardingStepStatus;
  /** Action button shown in the expanded body. Omit for steps with nothing to do. */
  cta?: OnboardingStepCta;
};

type OnboardingChecklistProps = Omit<BoxProps, 'title'> & {
  title?: string;
  steps?: OnboardingStep[];
  /** App slug — enables analytics tracking of checklist interactions. Omit to disable (e.g. Storybook). */
  appSlug?: string;
  /** Whether the panel starts expanded. Defaults to true; pass false to open collapsed (e.g. on the WFE page). */
  defaultExpanded?: boolean;
  /** Called when the user dismisses the completed checklist from the completion screen. */
  onFinish?: () => void;
  /** Called when the user permanently hides the checklist ("Don't show again"). Omit to hide the control. */
  onDismiss?: () => void;
};

const DEFAULT_STEPS: OnboardingStep[] = [
  {
    id: 'connect_repository',
    title: 'Connect your repository',
    description: "Your repository is connected — you're all set here.",
    status: 'completed',
  },
  {
    id: 'get_to_know_ci_config',
    title: 'Get to know your CI configuration',
    description: 'Edit your Workflow to get a successful build running.',
    status: 'active',
    cta: { label: 'Edit Workflow', onClick: () => {} },
  },
  {
    id: 'automate_builds',
    title: 'Automate your builds',
    description: 'Set up a trigger so Bitrise runs a build on every push.',
    status: 'upcoming',
    cta: { label: 'Set up a trigger', onClick: () => {} },
  },
];

const WidgetCircle = ({ children, ...boxProps }: BoxProps) => (
  <Box
    width="48"
    height="48"
    borderRadius="full"
    background="background/primary"
    borderWidth="1"
    borderStyle="solid"
    borderColor="border/minimal"
    boxShadow="elevation/md"
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink="0"
    overflow="hidden"
    {...boxProps}
  >
    {children}
  </Box>
);

const INTERCOM_POLL_INTERVAL_MS = 200;
const INTERCOM_POLL_MAX_ATTEMPTS = 100;

const isIntercomOnPage = () => typeof window.Intercom === 'function';

const setIntercomDefaultLauncherHidden = (isHidden: boolean | undefined) => {
  window.intercomSettings = { ...(window.intercomSettings ?? {}), hide_default_launcher: isHidden };
  window.Intercom('update', { hide_default_launcher: Boolean(isHidden) });
};

const RING_BOX = 48;
const RING_DIAMETER = 40;
const RING_STROKE = 4;
const RING_RADIUS = (RING_DIAMETER - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const RING_CENTER = RING_BOX / 2;

const RingGraphic = ({ completed, total }: { completed: number; total: number }) => {
  const progress = total > 0 ? completed / total : 0;
  const dash = RING_CIRCUMFERENCE * progress;
  const isComplete = total > 0 && completed >= total;

  return (
    <Box position="relative" width="full" height="full" display="flex" alignItems="center" justifyContent="center">
      <svg width={RING_BOX} height={RING_BOX} viewBox={`0 0 ${RING_BOX} ${RING_BOX}`}>
        <chakra.circle
          cx={RING_CENTER}
          cy={RING_CENTER}
          r={RING_RADIUS}
          fill="none"
          stroke="border/minimal"
          strokeWidth={RING_STROKE}
          _groupHover={{ stroke: 'border/strong' }}
        />
        <chakra.circle
          cx={RING_CENTER}
          cy={RING_CENTER}
          r={RING_RADIUS}
          fill="none"
          stroke="icon/positive"
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${RING_CIRCUMFERENCE - dash}`}
          transform={`rotate(-90 ${RING_CENTER} ${RING_CENTER})`}
        />
      </svg>
      <IconCheck
        position="absolute"
        insetInline="0"
        insetBlock="0"
        margin="auto"
        size="24"
        color={isComplete ? 'icon/positive' : 'icon/tertiary'}
        _groupHover={isComplete ? undefined : { color: 'icon/secondary' }}
      />
    </Box>
  );
};

// Completed steps get a check-circle icon; everything else shows a dashed circle.
const leadingIconFor = (step: OnboardingStep) =>
  step.status === 'completed' ? IconCheckCircleFilled : IconCircleDashed;

type ChecklistItemProps = {
  step: OnboardingStep;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
};

const ChecklistItem = ({ step, isExpanded, onExpandedChange }: ChecklistItemProps) => (
  <BitkitExpandableCard
    size="lg"
    elevation={false}
    title={step.title}
    icon={leadingIconFor(step)}
    iconColor={step.status === 'completed' ? 'icon/positive' : undefined}
    expanded={isExpanded}
    onChange={onExpandedChange}
    footer={
      step.cta ? (
        step.cta.href ? (
          <BitkitButton
            variant="secondary"
            size="sm"
            suffixIcon={IconArrowRight}
            href={step.cta.href}
            onClick={step.cta.onClick}
          >
            {step.cta.label}
          </BitkitButton>
        ) : (
          <BitkitButton variant="secondary" size="sm" suffixIcon={IconArrowRight} onClick={step.cta.onClick}>
            {step.cta.label}
          </BitkitButton>
        )
      ) : undefined
    }
  >
    <Text textStyle="body/md/regular" color="text/body">
      {step.description}
    </Text>
  </BitkitExpandableCard>
);

const CompletedScreen = ({ onFinish }: { onFinish?: () => void }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    textAlign="center"
    gap="12"
    paddingInline="32"
    paddingBlockEnd="32"
  >
    <Image src={onboardingCompleteIllustration} alt="" width="128" height="128" />
    <Box display="flex" flexDirection="column" gap="8" width="full">
      <Text textStyle="heading/h3" color="text/primary" textWrap="balance">
        All done! Happy building!
      </Text>
      <Text textStyle="body/md/regular" color="text/primary">
        If you get stuck, reach out to the{' '}
        <BitkitLink colorVariant="purple" href={URLs.COMMUNITY_FORUM} isExternal>
          community
        </BitkitLink>
        ,{' '}
        <BitkitLink colorVariant="purple" href={URLs.DOCUMENTATION} isExternal>
          read the docs
        </BitkitLink>
        , or install the{' '}
        <BitkitLink colorVariant="purple" href={URLs.BITRISE_MCP} isExternal>
          Bitrise MCP
        </BitkitLink>
        .
      </Text>
    </Box>
    <BitkitButton variant="secondary" size="lg" onClick={onFinish}>
      Close
    </BitkitButton>
  </Box>
);

const OnboardingChecklist = ({
  title = 'Get started with Bitrise CI',
  steps = DEFAULT_STEPS,
  appSlug,
  defaultExpanded = true,
  onFinish,
  onDismiss,
  ...boxProps
}: OnboardingChecklistProps) => {
  const activeStep = steps.find((step) => step.status === 'active') ?? steps[0];
  const [openId, setOpenId] = useState<string | null>(activeStep?.id ?? null);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isIntercomLoaded, setIsIntercomLoaded] = useState(isIntercomOnPage);

  const completedCount = steps.filter((step) => step.status === 'completed').length;
  const isAllComplete = steps.length > 0 && completedCount === steps.length;
  const totalSteps = steps.length;

  // "Displayed" fires when the panel is actually visible on page render. If it starts collapsed
  // (e.g. the WFE page), the panel isn't shown yet — opening it from the launcher fires it below.
  useEffect(() => {
    if (!appSlug || !defaultExpanded) {
      return;
    }
    trackChecklistDisplayed({ appSlug, totalSteps, completedSteps: completedCount, source: 'page_load' });
    // Mount-only; the completed count is intentionally the value at first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSlug]);

  // Keep the open item on the active step, but only when it completes IN PLACE while open (e.g.
  // the green-build poll advances progress) — compared against the previous render's steps, not
  // just the current status, so deliberately opening an already-completed step isn't force-closed.
  const prevStepsRef = useRef(steps);
  useEffect(() => {
    if (openId !== null) {
      const prevOpenStep = prevStepsRef.current.find((step) => step.id === openId);
      const openStep = steps.find((step) => step.id === openId);
      const justCompletedInPlace =
        Boolean(prevOpenStep) && prevOpenStep?.status !== 'completed' && openStep?.status === 'completed';
      if (!openStep || justCompletedInPlace) {
        setOpenId(activeStep?.id ?? null);
      }
    }
    prevStepsRef.current = steps;
  }, [steps, openId, activeStep?.id]);

  useEffect(() => {
    let previousHiddenSetting: boolean | undefined;
    let hasHiddenLauncher = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const hideDefaultLauncher = () => {
      previousHiddenSetting = window.intercomSettings?.hide_default_launcher;
      setIntercomDefaultLauncherHidden(true);
      hasHiddenLauncher = true;
    };

    const waitForIntercom = () => {
      timeoutId = setTimeout(() => {
        attempts += 1;
        if (isIntercomOnPage()) {
          hideDefaultLauncher();
          setIsIntercomLoaded(true);
        } else if (attempts < INTERCOM_POLL_MAX_ATTEMPTS) {
          waitForIntercom();
        }
      }, INTERCOM_POLL_INTERVAL_MS);
    };

    if (isIntercomOnPage()) {
      hideDefaultLauncher();
    } else {
      waitForIntercom();
    }

    return () => {
      clearTimeout(timeoutId);
      if (hasHiddenLauncher && isIntercomOnPage()) {
        setIntercomDefaultLauncherHidden(previousHiddenSetting);
      }
    };
  }, []);

  const collapse = () => {
    if (appSlug) {
      trackChecklistCollapsed({ appSlug, totalSteps, completedSteps: completedCount });
    }
    setIsExpanded(false);
  };

  const handleLauncherClick = () => {
    if (isExpanded) {
      collapse();
      return;
    }
    if (appSlug) {
      trackChecklistDisplayed({
        appSlug,
        totalSteps,
        completedSteps: completedCount,
        source: 'onboarding_checklist_launcher',
      });
    }
    setIsExpanded(true);
  };

  // A bottom-right stack: the panel (when open) floats above a pill holding the checklist
  // launcher, which toggles between the progress ring (collapsed) and an X (expanded), and
  // the Intercom launcher that replaces Intercom's own while the checklist is on screen.
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap="12" {...boxProps}>
      {isExpanded && (
        <Card.Root width="min(376px, 100%)" overflow="hidden">
          <Box display="flex" alignItems="flex-start" gap="12" paddingInline="16" paddingBlock="16">
            <WidgetCircle>
              <RingGraphic completed={completedCount} total={steps.length} />
            </WidgetCircle>
            <Box flex="1">
              <Text textStyle="heading/h3" color="text/primary">
                {title}
              </Text>
              <Text textStyle="body/md/regular" color="text/secondary">
                {completedCount}/{steps.length} complete
              </Text>
            </Box>
            {onDismiss && (
              <BitkitActionMenu
                size="md"
                trigger={<BitkitControlButton icon={IconMoreVertical} size="sm" label="More options" />}
              >
                <BitkitActionMenu.Item value="hide-permanently" icon={IconEyeSlash} onClick={onDismiss}>
                  Hide permanently
                </BitkitActionMenu.Item>
              </BitkitActionMenu>
            )}
          </Box>

          {isAllComplete ? (
            <CompletedScreen onFinish={onFinish} />
          ) : (
            <Box display="flex" flexDirection="column" gap="12" paddingInline="16" paddingBlockEnd="32">
              {steps.map((step, index) => (
                <ChecklistItem
                  key={step.id}
                  step={step}
                  isExpanded={openId === step.id}
                  onExpandedChange={(expanded) => {
                    setOpenId(expanded ? step.id : null);
                    // Only user-initiated expands reach here; the active step's auto-expand on
                    // mount is initial state and never calls onChange, so it's correctly excluded.
                    if (expanded && appSlug) {
                      trackChecklistItemExpanded({
                        appSlug,
                        totalSteps,
                        step: step.id as OnboardingChecklistStepId,
                        stepPosition: index + 1,
                      });
                    }
                  }}
                />
              ))}
            </Box>
          )}
        </Card.Root>
      )}

      <Box
        display="flex"
        flexDirection="column"
        gap="4"
        padding="4"
        borderRadius={rem(28)}
        background="background/primary"
      >
        <WidgetCircle
          asChild
          className="group"
          cursor="pointer"
          _hover={{ background: 'background/tertiary', borderColor: 'border/regular' }}
        >
          <button
            type="button"
            aria-label={isExpanded ? 'Collapse onboarding checklist' : 'Open onboarding checklist'}
            onClick={handleLauncherClick}
          >
            {isExpanded ? (
              <IconCross color="icon/primary" />
            ) : (
              <RingGraphic completed={completedCount} total={steps.length} />
            )}
          </button>
        </WidgetCircle>
        {isIntercomLoaded && (
          <WidgetCircle
            asChild
            cursor="pointer"
            borderWidth="0"
            background="button/primary/bg"
            boxShadow="elevation/sm"
            _hover={{ background: 'button/primary/bg-hover' }}
            _active={{ background: 'button/primary/bg-active' }}
          >
            <button type="button" aria-label="Chat with support" onClick={() => window.Intercom('show')}>
              <IconBitbotIntercomWhite />
            </button>
          </WidgetCircle>
        )}
      </Box>
    </Box>
  );
};

export default OnboardingChecklist;
